import React, { useCallback, useEffect } from "react";
import { useLocation } from "@reach/router";
import { useTranslation } from "react-i18next";
import { ALL_LANGUAGES, initBrowserI18n } from "@src/i18next";
import { formatLocale } from "@src/i18next/utils";
import { getNamespaceForBuildType } from "@src/i18next/resources";
import { getBuildWebiste } from "@src/utils/env";

const formatLang = (lang: string) => {
  if (!lang) return lang;

  const [p1, p2] = lang.split("-");

  if (!p2) return p1;

  return `${p1}-${p2.toUpperCase()}`;
};

const isValidLanguage = (lang: string) => {
  return (
    ALL_LANGUAGES.includes(lang) ||
    ALL_LANGUAGES.includes(lang.toLowerCase()) ||
    ALL_LANGUAGES.includes(lang.toUpperCase())
  );
};

const LanguageDetector = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const buildType = getBuildWebiste();
  const namespace = getNamespaceForBuildType(buildType);

  const loadResources = useCallback(async () => {
    const pathSegments = location.pathname.split("/");
    const langFromPath = formatLang(pathSegments[1]);

    if (!langFromPath || !isValidLanguage(langFromPath)) {
      return;
    }

    const currentLanguage = (i18n.language || "").toLowerCase();
    if (currentLanguage === langFromPath.toLowerCase()) {
      return;
    }

    const formattedLang = langFromPath.includes("-") ? formatLocale(langFromPath.toLowerCase()) : langFromPath;

    try {
      await initBrowserI18n(formattedLang, namespace);
    } catch (error) {
      console.error("Failed to switch language after path change", formattedLang, error);
    }
  }, [i18n, location.pathname, namespace]);

  useEffect(() => {
    void loadResources();
  }, [loadResources]);

  return null;
};

export default LanguageDetector;
